/**
 * Device Fingerprint Generator
 * Creates a unique-ish identifier for the current browser/device combination
 * This is NOT for security purposes, just for distinguishing devices
 */

import { STORAGE_KEYS } from "@/constants";

async function getCanvasFingerprint(): Promise<string> {
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";

    // Draw some text and shapes
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("GoodBaby 👶", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("GoodBaby 👶", 4, 17);

    return canvas.toDataURL();
  } catch {
    return "";
  }
}

async function getWebGLFingerprint(): Promise<string> {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return "";

    const debugInfo = (gl as WebGLRenderingContext).getExtension(
      "WEBGL_debug_renderer_info"
    );
    if (!debugInfo) return "";

    const vendor = (gl as WebGLRenderingContext).getParameter(
      debugInfo.UNMASKED_VENDOR_WEBGL
    );
    const renderer = (gl as WebGLRenderingContext).getParameter(
      debugInfo.UNMASKED_RENDERER_WEBGL
    );

    return `${vendor}~${renderer}`;
  } catch {
    return "";
  }
}

function getNavigatorInfo(): string {
  const nav = navigator;
  return [
    nav.userAgent,
    nav.language,
    nav.platform,
    nav.hardwareConcurrency?.toString() || "",
    nav.maxTouchPoints?.toString() || "",
    screen.colorDepth?.toString() || "",
    screen.width?.toString() || "",
    screen.height?.toString() || "",
    new Date().getTimezoneOffset().toString(),
  ].join("|");
}

async function hashString(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Generate a device fingerprint
 * Combines multiple browser signals to create a unique identifier
 */
export async function generateFingerprint(): Promise<string> {
  const components: string[] = [];

  // Collect fingerprint components
  components.push(getNavigatorInfo());
  components.push(await getCanvasFingerprint());
  components.push(await getWebGLFingerprint());

  // Combine and hash
  const combined = components.join("###");
  const hash = await hashString(combined);

  // Return first 32 characters
  return hash.substring(0, 32);
}

/**
 * Get or create a cached fingerprint
 * Stores in localStorage for consistency
 */

export async function getDeviceFingerprint(): Promise<string> {
  // Check if we have a cached fingerprint
  const cached = localStorage.getItem(STORAGE_KEYS.deviceFingerprint);
  if (cached) {
    return cached;
  }

  // Generate new fingerprint
  const fingerprint = await generateFingerprint();
  localStorage.setItem(STORAGE_KEYS.deviceFingerprint, fingerprint);
  return fingerprint;
}
